import React, {FC, useState} from "react";
import {Tooltip} from "antd";
import moment from "moment";
import MoreMenu from "../../bookings/BookingList/MoreMenu/MoreMenu";
import ReverseBookingModal from "../../bookings/BookingList/ReverseBookingModal";
import {GetBookingByIdQuery, GetBookingByIdWithCourseLessonBookingsQuery} from "../../../generated/graphql";
import i18n from "../../../services/i18n";
import {TableType} from "../../bookings/BookingList/BookingListColumns";
import LessonBookingAdjustmentModal from "./LessonBookingAdjustmentModal";
import ResetPaymentPlanModal from "./ResetPaymentPlanModal";
import {canAdjustLessonBooking} from "./lessonBookingAdjustEligibility";
import TransferAttendeeToAnotherCourse from "../../bookings/TransferAttendeeToAnotherCourse";
import {isBookingSettled} from "../../../helpers/bookingDetailsRouting";
import Roles, {hasRoleAtLeast} from "../../../../shared/models/roles";
import {useCurrentUserRoles} from "../../../helpers/useIsStaffOnlyAccess";
import useTransferWizardMode from "../../../hooks/useTransferWizardMode";
import {useStaffVisibility} from "../../../hooks/useStaffVisibility";
import {StaffVisibilityLevel} from "../../../../shared/models/staffVisibility";

type BookingData = GetBookingByIdQuery["bookingById"] | GetBookingByIdWithCourseLessonBookingsQuery["bookingByIdWithCourseLessonBookings"];

type MoreMenuSettlementDetailsProps = {
    booking?: BookingData;
    refetch: () => void;
};

const MoreMenuSettlementDetails: FC<MoreMenuSettlementDetailsProps> = ({booking, refetch}) => {
    const [reverseBookingModalVisible, setReverseBookingModalVisible] = useState(false);
    const [adjustModalVisible, setAdjustModalVisible] = useState(false);
    const [resetPaymentPlanModalVisible, setResetPaymentPlanModalVisible] = useState(false);
    const [transferModalOpen, setTransferModalOpen] = useState(false);
    const [copyModalOpen, setCopyModalOpen] = useState(false);
    const {roles} = useCurrentUserRoles();
    const {wizardMode: transferWizardMode} = useTransferWizardMode();
    // Sichtbarkeitsregel „Buchungsaktionen: Nein“ (#1188): kein Verschieben/Kopieren für Mitarbeitende.
    const bookingActionsDisabled = useStaffVisibility("bookingActions") === StaffVisibilityLevel.NoAccess;

    if (!booking) return null;

    const isCanceled = booking.canceled !== null && booking.canceled !== undefined;
    // Storno und Zahlplan-Reset brauchen den Beleg selbst; Umbuchen/Kopieren nur den Abrechnungsstatus (#709).
    const hasInvoice = Boolean(booking.invoice?.id);
    const isSettled = isBookingSettled(booking);
    const cancelDate = booking.canceled ? moment(booking.canceled).format("DD.MM.YYYY") : "";

    // Nach DSGVO-Löschantrag lehnt der Server Storno, Umbuchen, Kopieren und Änderungen am Zahlungsplan ab.
    const attendeeErased = Boolean(booking.attendee?.erasedAt);
    const adjustDisabled = attendeeErased || !canAdjustLessonBooking(booking);
    // sc-3031: Reset nur für abgerechnete Lesson-Bookings (nicht canceled, mit Invoice,
    // Kurs erlaubt Einzelbuchungen).
    const allowLessonBooking = Boolean(booking.courseLessons?.[0]?.course?.allowLessonBooking);
    const resetDisabled = attendeeErased || !hasInvoice || isCanceled || !allowLessonBooking;

    // Adapter: Booking-Daten in das TableType-Format für ReverseBookingModal.
    // ReverseBookingModal nutzt record.invoice?.id, record.courseId und record.attendee?.booker?.email.
    // BookingData ist strukturell kompatibel mit TableType für diese Felder,
    // aber die Typen sind aus verschiedenen Queries generiert und daher nicht direkt zuweisbar.
    const recordForModal = {
        ...booking,
        courseId: booking.courseId,
        invoice: booking.invoice ?? undefined,
        attendee: booking.attendee ?? undefined,
        canceled: booking.canceled ?? null,
    } as unknown as TableType;

    // Gleiche Sichtbarkeitsregeln wie in der Buchungsliste (RenderActions.getMenuItems):
    // Legacy-Umbuchen und Kopieren sind für alle Zugänge frei (STAFF_WRITE_ALLOWLIST). Im
    // Wizard-Modus läuft Umbuchen über die Engine, dort gelten für Mitarbeitende die E1-Regeln (#660).
    const canUseLegacyTransfer = hasRoleAtLeast(roles, Roles.Admin) || transferWizardMode !== true;
    const canUseEngineTransfer = hasRoleAtLeast(roles, Roles.Instructor);
    const courseId = booking.courseId ?? "";
    const attendeeFullName = `${booking.attendee?.firstname ?? ""} ${booking.attendee?.lastname ?? ""}`.trim();

    // Der Dialog storniert die Quellbuchung selbst (Storno-Rechnung) und meldet nur „zu";
    // ohne Refetch bliebe die Seite auf dem alten Stand und würde Verschieben erneut anbieten.
    const closeTransferModal = (setOpen: (open: boolean) => void) => (open: boolean) => {
        setOpen(open);
        if (!open) refetch();
    };

    const transferMenuItems = [];

    // Die Engine blockiert Mitarbeitende, sobald Geld im Spiel ist (transferState.ts
    // `sourceHasMoney` = Rechnung ODER Zahlungseingang/Erstattung auf einer Rate);
    // ohne dieses Gate wäre der Eintrag hier eine Sackgasse. Bewusst etwas strenger als der
    // Server (gelöschte Raten zählen mit), lieber verstecken als ins Leere laufen lassen.
    const sourceHasMoney =
        isSettled ||
        (booking.scheduledPayments ?? []).some((sp) => sp.paymentDone != null || Number(sp.refundedAmount ?? 0) > 0 || (sp.openAmount != null && sp.openAmount < sp.paymentAmount));
    const canUseEngineTransferHere = canUseEngineTransfer && transferWizardMode === true && !sourceHasMoney;
    const isPlaceReservation = booking.bookingType === "PlaceReservation";

    const mayTransferOrCopy = !isCanceled && !attendeeErased && !isPlaceReservation && !bookingActionsDisabled;

    if (mayTransferOrCopy && (canUseLegacyTransfer || canUseEngineTransferHere)) {
        transferMenuItems.push({
            key: "transfer-attendee",
            label: i18n.containers.invoice.SettlementDetails.moreMenu.transferAttendee(),
            onClick: () => setTransferModalOpen(true),
        });
    }

    if (mayTransferOrCopy) {
        transferMenuItems.push({
            key: "copy-attendee",
            label: i18n.containers.invoice.SettlementDetails.moreMenu.copyAttendee(),
            onClick: () => setCopyModalOpen(true),
        });
    }

    const moreMenuItems = [
        ...transferMenuItems,
        {
            key: "cancelBooking",
            label: isCanceled ? i18n.containers.bookings.BookingList.bookingReversal.disabledText({cancelDate}) : i18n.containers.bookings.BookingList.reverseBooking(),
            disabled: isCanceled || !hasInvoice || attendeeErased,
            onClick: () => setReverseBookingModalVisible(true),
        },
        {
            key: "adjustLessonBooking",
            label: i18n.containers.invoice.SettlementDetails.moreMenu.adjustLessonBooking(),
            disabled: adjustDisabled,
            onClick: () => setAdjustModalVisible(true),
        },
        {
            key: "resetPaymentPlan",
            label: (
                <Tooltip title={i18n.containers.invoice.SettlementDetails.moreMenu.resetPaymentPlanTooltip()} placement="left">
                    <span>{i18n.containers.invoice.SettlementDetails.moreMenu.resetPaymentPlanToCourseStandard()}</span>
                </Tooltip>
            ),
            disabled: resetDisabled,
            onClick: () => setResetPaymentPlanModalVisible(true),
        },
    ];

    // Type-Cast: LessonBookingAdjustmentModal erwartet die WithCourseLessonBookings-Variante.
    // SettlementDetails.tsx verwendet diese Query bereits ausschliesslich, daher ist der Cast sicher.
    const bookingForAdjustment = booking as GetBookingByIdWithCourseLessonBookingsQuery["bookingByIdWithCourseLessonBookings"];

    return (
        <>
            <ReverseBookingModal
                open={reverseBookingModalVisible}
                closeModal={() => setReverseBookingModalVisible(false)}
                confirmAction={() => setReverseBookingModalVisible(false)}
                record={recordForModal}
                refetch={refetch}
            />
            {adjustModalVisible && (
                <LessonBookingAdjustmentModal
                    open={adjustModalVisible}
                    onClose={() => setAdjustModalVisible(false)}
                    booking={bookingForAdjustment}
                    onSuccess={refetch}
                />
            )}
            {resetPaymentPlanModalVisible && booking.id && (
                <ResetPaymentPlanModal
                    open={resetPaymentPlanModalVisible}
                    bookingId={booking.id}
                    onClose={() => setResetPaymentPlanModalVisible(false)}
                    onSuccess={refetch}
                />
            )}
            {transferModalOpen && booking.id && (
                <TransferAttendeeToAnotherCourse
                    transformAction="transfer"
                    bookingId={booking.id}
                    currentCourseId={courseId}
                    modalOpen={transferModalOpen}
                    setModalOpen={closeTransferModal(setTransferModalOpen)}
                    attendeeFullName={attendeeFullName}
                    attendeeId={booking.attendee?.id ?? undefined}
                    isSettled={isSettled}
                />
            )}
            {copyModalOpen && booking.id && (
                <TransferAttendeeToAnotherCourse
                    transformAction="copy"
                    bookingId={booking.id}
                    currentCourseId={courseId}
                    modalOpen={copyModalOpen}
                    setModalOpen={closeTransferModal(setCopyModalOpen)}
                    attendeeFullName={attendeeFullName}
                    attendeeId={booking.attendee?.id ?? undefined}
                    isSettled={isSettled}
                />
            )}
            <MoreMenu moreMenuItems={moreMenuItems} buttonType="link" />
        </>
    );
};

export default MoreMenuSettlementDetails;
